<template lang="pug">
  b-modal(
    v-model="show",
    size="lg",
    header-class="lmm-common-popup-header",
    dialog-class="mdm-upload-dialog",
    :title="$t('mdm_page.add_app')",
    :no-close-on-backdrop="uploading",
    :no-close-on-esc="uploading",
    :hide-header-close="uploading",
    @hidden="reset")
    b-form
      //- 설치파일 선택 — accept으로 플랫폼 확장자만, 선택 시 한 번 더 검증(.xapk 등 차단)
      b-form-group(:label="$t('mdm_page.install_file')")
        b-form-file(
          v-model="selectedFile",
          :accept="acceptAttr",
          :placeholder="$t('mdm_page.file_placeholder')",
          :browse-text="$t('mdm_page.file_browse')",
          @input="onFileChange")
        b-form-text {{$t('mdm_page.allowed_ext', { ext: allowedExtText })}}
      //- 앱 이름: Android는 APK 라벨로 서버가 채우므로 입력칸 숨김(버전과 동일). Win/macOS만 입력
      b-form-group(v-if="!isAndroid", :label="$t('mdm_page.app_name')")
        b-form-input(v-model="form.app_name", :maxlength="256", size="sm")
        b-form-text {{$t('mdm_page.app_name_help')}}
      //- Android: 앱 이름·버전·패키지는 APK에서 자동추출 → 입력칸 없이 안내만
      b-alert.py-2(v-if="isAndroid", show, variant="info") {{$t('mdm_page.apk_auto_extract')}}
      //- Windows·macOS: 표시 버전 필수
      b-form-group(v-if="!isAndroid", :label="$t('version')")
        b-form-input(v-model="form.version", :maxlength="48", size="sm")
      //- 설치 옵션 — 무인 설치(Win, 선택)·설치 후 실행(전 OS). 박스 없이 라벨로 묶음.
      b-form-group(:label="$t('mdm_page.install_options')")
        //- 무인 설치 (Windows) — 토글 + 우측 괄호 안내. flex-wrap으로 좁으면 줄바꿈
        div.d-flex.align-items-center.flex-wrap(v-if="isWindows")
          b-form-checkbox(v-model="silentEnabled", switch, :disabled="!canSilent") {{$t('mdm_page.silent_enable')}}
          small.ml-1.text-success(v-if="canSilent") ({{$t('mdm_page.installer_detected_short', { name: detectedLabel })}})
          small.ml-1.text-muted(v-else-if="!selectedFile") ({{$t('mdm_page.silent_select_file_first')}})
          small.ml-1.text-warning(v-else) ({{$t('mdm_page.installer_not_detected')}})
        b-form-checkbox.mt-2(v-model="form.run_after_install", switch) {{$t('mdm_page.run_after_install')}}
      //- 업로드 진행률 (대용량 대비)
      div.mt-2(v-if="uploading")
        b-progress(:value="uploadProgress", :max="100", show-progress, animated)
    template(slot="modal-footer")
      b-btn(size="sm", variant="primary", :disabled="uploading", @click="submit")
        | {{uploading ? $t('mdm_page.uploading') : $t('mdm_page.upload')}}
      b-btn(size="sm", variant="secondary", :disabled="uploading", @click="show = false") {{$t('cancel')}}
</template>

<script>
// 앱(설치파일) 업로드 모달 — 플랫폼별 폼 + multipart 업로드 + 진행률.
// Windows 무인 설치: 인스톨러 종류를 파일 시그니처로 자동 감지하고, 무인 인자는 토글로 켤 때만 적용.
// (자동으로 켜지 않음 — 기본 off. 감지 실패면 무인 불가라 토글 비활성)
import { MdmAppAPI } from "@/api/mdm"

// 플랫폼별 허용 확장자 (백엔드 ALLOWED_EXTENSIONS와 일치해야 함)
const PLATFORM_EXT = {
  android: ["apk"],
  windows: ["exe", "msi"],
  osx: ["pkg", "dmg"],
}

// Windows 인스톨러별 무인 설치 인자 + 자동 감지용 시그니처(파일 안 ASCII 문자열).
// msi는 확장자로 판별, exe는 바이너리에 박힌 제작 도구 문자열로 감지.
const WINDOWS_INSTALLERS = [
  { key: "msi", args: "/quiet /norestart", sig: null },
  { key: "nsis", args: "/S", sig: "Nullsoft" },
  { key: "inno", args: "/VERYSILENT /SUPPRESSMSGBOXES /NORESTART", sig: "Inno Setup" },
  { key: "installshield", args: '/s /v"/qn"', sig: "InstallShield" },
]
// 감지 시 파일 앞·끝에서 읽을 바이트 수 (NSIS는 끝 오버레이, Inno는 앞부분에 시그니처)
const SNIFF_BYTES = 1024 * 1024

const emptyForm = () => ({
  app_name: "",
  version: "",
  run_after_install: false,
})

export default {
  name: "MdmAppUploadModal",
  data() {
    return {
      show: false,
      platform: "windows",
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      silentEnabled: false, // 무인 설치 토글 (기본 off — 자동으로 켜지 않음)
      detectedKey: null, // 자동 감지된 인스톨러 종류 (null = 감지 실패 → 무인 불가)
      form: emptyForm(),
    }
  },
  computed: {
    isAndroid() {
      return this.platform === "android"
    },
    isWindows() {
      return this.platform === "windows"
    },
    allowedExt() {
      return PLATFORM_EXT[this.platform] || []
    },
    acceptAttr() {
      return this.allowedExt.map(e => `.${e}`).join(",")
    },
    allowedExtText() {
      return this.allowedExt.join(", ")
    },
    detectedLabel() {
      return this.detectedKey ? this.$t(`mdm_page.installer_${this.detectedKey}`) : ""
    },
    // 무인 설치 가능 여부: 파일 선택 + 인스톨러 종류 감지 성공 (감지 실패면 토글 비활성)
    canSilent() {
      return !!this.selectedFile && !!this.detectedKey
    },
    // 실제 전송할 무인 설치 인자: 토글 on + 감지 성공일 때만 (감지된 종류의 프리셋)
    effectiveSilentArgs() {
      if (!this.silentEnabled || !this.detectedKey) return ""
      const preset = WINDOWS_INSTALLERS.find(i => i.key === this.detectedKey)
      return preset ? preset.args : ""
    },
  },
  methods: {
    // 부모가 ref로 호출 — platform = 현재 탭(windows/android/osx)
    open(platform) {
      this.platform = platform
      this.resetState()
      this.show = true
    },
    reset() {
      this.resetState()
    },
    resetState() {
      this.selectedFile = null
      this.uploading = false
      this.uploadProgress = 0
      this.silentEnabled = false
      this.detectedKey = null
      this.form = emptyForm()
    },
    // 파일 선택 시: 확장자 검증(.xapk 등 차단) → app_name 자동채움 → (Windows면) 인스톨러 자동 감지
    async onFileChange(file) {
      if (!file) return
      const ext = (file.name.split(".").pop() || "").toLowerCase()
      if (!this.allowedExt.includes(ext)) {
        this.$awn.warning(this.$t("mdm_page.invalid_ext", { ext: this.allowedExtText }))
        this.selectedFile = null
        return
      }
      if (!this.form.app_name) {
        this.form.app_name = file.name.replace(/\.[^.]+$/, "")
      }
      if (this.isWindows) {
        await this.detectInstaller(file, ext)
      }
    },
    // 파일 앞·끝 일부를 읽어 시그니처로 인스톨러 종류를 감지.
    // 자동으로 토글을 켜지 않는다(기본 off). 감지 실패(무인 불가)면 토글을 강제로 끈다.
    async detectInstaller(file, ext) {
      this.detectedKey = null
      if (ext === "msi") {
        this.detectedKey = "msi"
      } else {
        try {
          const text = await this.sniffText(file)
          const hit = WINDOWS_INSTALLERS.find(i => i.sig && text.includes(i.sig))
          this.detectedKey = hit ? hit.key : null
        } catch (e) {
          this.detectedKey = null
        }
      }
      if (!this.detectedKey) this.silentEnabled = false
    },
    // 파일 앞 SNIFF_BYTES + 끝 SNIFF_BYTES를 latin1 텍스트로 읽어 합침 (시그니처는 ASCII)
    sniffText(file) {
      const read = blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () =>
            resolve(new TextDecoder("latin1").decode(new Uint8Array(reader.result)))
          reader.onerror = reject
          reader.readAsArrayBuffer(blob)
        })
      const head = read(file.slice(0, SNIFF_BYTES))
      const tail = file.size > SNIFF_BYTES ? read(file.slice(file.size - SNIFF_BYTES)) : Promise.resolve("")
      return Promise.all([head, tail]).then(([h, t]) => h + t)
    },
    validate() {
      if (!this.selectedFile) {
        this.$awn.warning(this.$t("mdm_page.file_required"))
        return false
      }
      // 앱 이름은 Win/macOS만 필수 (Android는 입력칸을 숨기고 APK 라벨로 서버가 채움)
      if (!this.isAndroid && !this.form.app_name.trim()) {
        this.$awn.warning(this.$t("mdm_page.app_name_required"))
        return false
      }
      // Windows·macOS는 표시 버전 필수 (Android는 APK 자동추출)
      if (!this.isAndroid && !this.form.version.trim()) {
        this.$awn.warning(this.$t("mdm_page.version_required"))
        return false
      }
      return true
    },
    buildFormData() {
      const fd = new FormData()
      fd.append("file", this.selectedFile)
      fd.append("platform", this.platform)
      fd.append("app_name", this.form.app_name.trim())
      // version: Windows·macOS만 (Android는 백엔드가 APK에서 추출)
      if (!this.isAndroid && this.form.version) {
        fd.append("version", this.form.version.trim())
      }
      // silent_install_args: Windows + 무인 설치 토글 on + 감지 성공 시에만
      const silentArgs = this.effectiveSilentArgs
      if (this.isWindows && silentArgs) {
        fd.append("silent_install_args", silentArgs)
      }
      fd.append("run_after_install", this.form.run_after_install)
      return fd
    },
    async submit() {
      if (this.uploading) return
      if (!this.validate()) return
      this.uploading = true
      this.uploadProgress = 0
      try {
        await MdmAppAPI.post("", this.buildFormData(), {
          // Content-Type은 지정하지 않음 — axios가 FormData의 multipart boundary를 자동 설정
          onUploadProgress: e => {
            if (e.total) this.uploadProgress = Math.round((e.loaded / e.total) * 100)
          },
        })
        this.$awn.success(this.$t("mdm_page.app_uploaded"))
        this.show = false
        this.$emit("uploaded")
      } catch (error) {
        // 에러 토스트는 전역 인터셉터가 처리 (확장자·크기·APK파싱 400 / 동시 업로드 409)
      } finally {
        this.uploading = false
      }
    },
  },
}
</script>

<!-- 모달은 렌더 시 <body>로 이동해 scoped CSS가 안 닿음 → non-scoped + 고유 클래스 -->
<style lang="scss">
.mdm-upload-dialog {
  margin-top: 8vh;

  // 폰: 풀스크린으로 꽉 채우지 않고 상단 여백만 줄여 화면에 맞춤 (모바일 표준 §10)
  @media (max-width: 575px) {
    margin-top: 1rem;
  }

  // 비활성(disabled) 토글을 off 상태와 확실히 구분 — 더 흐리게
  .custom-control-input:disabled ~ .custom-control-label {
    opacity: 0.4;
  }
}
</style>
